/**
 * Mock track generators for cinematic debugging.
 * URL param `?mockTrack=orbit|flythrough|figure8` triggers these.
 *
 * 输出 Track，作为 loadScene 输入由 viewer 闭环播放；keyframes.times 单位为秒。
 */
import { Vec3, type Track } from '@mindcloudxai_3d/core';
import type { MockTrackType } from '@hooks/useUrlParams';
import type { CinematicEvent } from '@app-types/scene';

// ---------------------------------------------------------------------------
// Track generators
// ---------------------------------------------------------------------------

interface MockTrackOptions {
  duration?: number;
  center?: Vec3;
  radius?: number;
}

const DEFAULT_CENTER = new Vec3(0, 1, 0);
const DEFAULT_RADIUS = 5;
const DEFAULT_DURATION = 10;

interface TrackSample {
  /** 归一化时间 0–1 */
  t: number;
  position: Vec3;
  target: Vec3;
}

function buildTrack(name: string, duration: number, samples: TrackSample[]): Track {
  const times: number[] = [];
  const position: number[] = [];
  const target: number[] = [];
  const fov: number[] = [];

  for (const sample of samples) {
    times.push(sample.t * duration);
    position.push(sample.position.x, sample.position.y, sample.position.z);
    target.push(sample.target.x, sample.target.y, sample.target.z);
    fov.push(60);
  }

  return {
    name,
    duration,
    loopMode: 'repeat',
    interpolation: 'spline',
    smoothness: 0,
    keyframes: { times, values: { position, target, fov } },
  };
}

/** 水平 360° 环绕轨迹 */
function generateOrbit(opts: MockTrackOptions): Track {
  const duration = opts.duration ?? DEFAULT_DURATION;
  const center = opts.center ?? DEFAULT_CENTER;
  const radius = opts.radius ?? DEFAULT_RADIUS;
  const count = 8;
  const samples: TrackSample[] = [];

  for (let i = 0; i <= count; i++) {
    const angle = (i / count) * Math.PI * 2;
    samples.push({
      t: i / count,
      position: new Vec3(
        center.x + Math.sin(angle) * radius,
        center.y + radius * 0.3,
        center.z + Math.cos(angle) * radius,
      ),
      target: center,
    });
  }

  return buildTrack('mock-orbit', duration, samples);
}

/** 直线穿越轨迹（从后方飞向前方，轻微抬升） */
function generateFlythrough(opts: MockTrackOptions): Track {
  const duration = opts.duration ?? DEFAULT_DURATION;
  const center = opts.center ?? DEFAULT_CENTER;
  const radius = opts.radius ?? DEFAULT_RADIUS;
  const positions: Vec3[] = [
    new Vec3(center.x, center.y + 0.5, center.z + radius * 2),
    new Vec3(center.x, center.y + 1.0, center.z + radius * 0.5),
    new Vec3(center.x, center.y + 1.5, center.z - radius * 0.5),
    new Vec3(center.x, center.y + 2.0, center.z - radius * 2),
  ];

  const samples: TrackSample[] = positions.map((pos, i) => ({
    t: i / (positions.length - 1),
    position: pos,
    target: positions[Math.min(i + 1, positions.length - 1)],
  }));

  return buildTrack('mock-flythrough', duration, samples);
}

/** 8 字形环绕轨迹（带高度起伏） */
function generateFigure8(opts: MockTrackOptions): Track {
  const duration = opts.duration ?? DEFAULT_DURATION;
  const center = opts.center ?? DEFAULT_CENTER;
  const radius = opts.radius ?? DEFAULT_RADIUS;
  const count = 12;
  const samples: TrackSample[] = [];

  for (let i = 0; i <= count; i++) {
    const angle = (i / count) * Math.PI * 2;
    // Lemniscate of Bernoulli (∞ shape)
    const sinA = Math.sin(angle);
    const cosA = Math.cos(angle);
    const denom = 1 + sinA * sinA;
    samples.push({
      t: i / count,
      position: new Vec3(
        center.x + (radius * cosA) / denom,
        center.y + 0.5 + Math.sin(angle * 2) * radius * 0.2,
        center.z + (radius * sinA * cosA) / denom,
      ),
      target: center,
    });
  }

  return buildTrack('mock-figure8', duration, samples);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function generateMockTrack(type: MockTrackType, options?: MockTrackOptions): Track {
  const opts = options ?? {};
  switch (type) {
    case 'orbit':
      return generateOrbit(opts);
    case 'flythrough':
      return generateFlythrough(opts);
    case 'figure8':
      return generateFigure8(opts);
    default:
      return generateOrbit(opts);
  }
}

/**
 * 与 mock 轨迹同源的示例事件——按归一化进度（0–1）放置三个 hotspot 触点，
 * 调用方根据实际 duration 解析为秒。这样事件密度跟轨迹时长解耦，便于本地联调。
 */
export function generateMockCinematicEvents(
  type: MockTrackType,
  duration: number = DEFAULT_DURATION,
): CinematicEvent[] {
  // 仅 orbit 与 figure8 与多 hotspot 联动，flythrough 仅放一次中段触发，演示自定义事件
  if (type === 'flythrough') {
    return [
      { t: duration * 0.5, type: 'custom', payload: { tag: 'midpoint' } },
      { t: duration * 0.9, type: 'hotspot', payload: { index: 0 } },
    ];
  }
  return [
    { t: duration * 0.25, type: 'hotspot', payload: { index: 0 } },
    { t: duration * 0.55, type: 'hotspot', payload: { index: 1 } },
    { t: duration * 0.85, type: 'hotspot', payload: { index: 2 } },
  ];
}
